import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import {
  DIALOG_COMPONENT_DATA,
  DialogComponent,
} from '@component/dialog/dialog.component';

export interface DocumentPreviewDialogData {
  fileName: string;
  fileType: string;
  downloadUrl: string;
}

@Component({
  selector: 'app-document-preview-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './document-preview-dialog.component.html',
  styleUrls: ['./document-preview-dialog.component.scss'],
})
export class DocumentPreviewDialogComponent implements OnInit, OnDestroy {
  loading = true;
  error = false;
  safeUrl: SafeResourceUrl | null = null;
  private blobUrl: string | null = null;

  constructor(
    @Inject(DIALOG_COMPONENT_DATA) public data: DocumentPreviewDialogData,
    private dialogRef: MatDialogRef<DialogComponent>,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.loadDocument();
  }

  ngOnDestroy(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }

  private async loadDocument(): Promise<void> {
    try {
      const response = await fetch(this.data.downloadUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }
      const blob = await response.blob();
      this.blobUrl = URL.createObjectURL(blob);
      // Blob URLs are safe - they're created from content we fetched
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
      this.loading = false;
    } catch {
      this.error = true;
      this.loading = false;
    }
  }

  // Strip file extension from filename for cleaner display
  get displayFileName(): string {
    const fileName = this.data.fileName;
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex > 0) {
      return fileName.substring(0, lastDotIndex);
    }
    return fileName;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onDownload(): void {
    this.dialogRef.close('download');
  }

  isPdfOrImage(): boolean {
    const type = this.data.fileType.toUpperCase();
    return ['PDF', 'PNG', 'JPG', 'JPEG', 'GIF', 'WEBP'].includes(type);
  }
}
